import { api } from './client';

export type PilotPhase = 'poc' | 'trial' | 'decision';
export type PilotOutcome = 'converted' | 'cancelled';

export interface PilotRow {
  installation_id: string;
  installation_name: string;
  product: string;
  organization_id: string;
  organization_name: string;
  organization_inn: string | null;
  contact_person: string | null;
  contact_email: string | null;
  bitrix_deal_url: string | null;
  started_at: string | null;
  planned_end_at: string | null;
  phase: PilotPhase | null;
  outcome: PilotOutcome | null;
  actual_end_at: string | null;
  extended_count: number;
  notes: string | null;
  days_left: number | null;
  is_active: boolean;
  tickets_total: number;
  tickets_open: number;
  last_ticket_at: string | null;
}

export interface PilotSummary {
  active: number;
  converted: number;
  cancelled: number;
  expiring_7d: number;
  overdue: number;
  no_end_date: number;
}

export interface PilotListParams {
  include_finished?: boolean;
  phase?: PilotPhase;
  outcome?: PilotOutcome;
  expiring_within_days?: number;
}

export type PipelineStatus = 'planned' | 'active' | 'completed' | 'declined' | 'frozen';

export interface MigrateResult {
  row: PilotRow;
  created_organization: boolean;
  created_installation: boolean;
  matched_existing_org: boolean;
  matched_organization_name: string | null;
  matched_organization_id: string | null;
  name_mismatch_warning: string | null;
}

export interface PipelineRow {
  bc_pilot_id: number;
  customer_name: string;
  customer_org: string | null;
  customer_inn: string | null;
  customer_fqdn: string | null;
  customer_contact_name: string | null;
  customer_contact_email: string | null;
  customer_contact_phone: string | null;
  deployment_type: string | null;
  b24_deal_url: string | null;
  status: PipelineStatus;
  first_contact_at: string | null;
  start_date: string | null;
  expected_end_date: string | null;
  actual_end_date: string | null;
  amount: number | null;
  result: string | null;
  comment: string | null;
  owner_name: string | null;
  owner_email: string | null;
  partner_present: boolean;
  partner_org: string | null;
  questionnaire: boolean;
  sd_organization_id: string | null;
  sd_installation_id: string | null;
  sd_organization_name: string | null;
  auto_match_warning: string | null;
}

export interface UnlinkResult {
  ok: boolean;
  removed_link: boolean;
  removed_installation_id: string | null;
  removed_organization_id: string | null;
  kept_organization_id: string | null;
  notes: string[];
}

export const pilotsApi = {
  list: (p?: PilotListParams) =>
    api.get<PilotRow[]>('/api/v1/pilots', { params: p }).then(r => r.data),
  summary: () =>
    api.get<PilotSummary>('/api/v1/pilots/summary').then(r => r.data),
  extend: (instId: string, p: { days: number; new_phase?: PilotPhase; note?: string }) =>
    api.post<PilotRow>(`/api/v1/pilots/${instId}/extend`, p).then(r => r.data),
  convert: (instId: string, note?: string) =>
    api.post<PilotRow>(`/api/v1/pilots/${instId}/convert`, { note }).then(r => r.data),
  cancel: (instId: string, note?: string) =>
    api.post<PilotRow>(`/api/v1/pilots/${instId}/cancel`, { note }).then(r => r.data),
  reopen: (instId: string, note?: string) =>
    api.post<PilotRow>(`/api/v1/pilots/${instId}/reopen`, { note }).then(r => r.data),
  pipeline: (status?: PipelineStatus, nocache = false) =>
    api.get<PipelineRow[]>('/api/v1/pilots/pipeline',
      { params: { ...(status ? { status } : {}), ...(nocache ? { nocache: 1 } : {}) } }).then(r => r.data),
  migrate: (p: {
    bc_pilot_id: number;
    pilot_phase?: PilotPhase;
    target_organization_id?: string;
    inn_override?: string;
    deployment_type?: 'tenant' | 'onprem';
    note?: string;
  }) =>
    api.post<MigrateResult>('/api/v1/pilots/pipeline/migrate', p).then(r => r.data),
  link: (bc_pilot_id: number, p: { organization_id: string; installation_id?: string; note?: string }) =>
    api.post<{ ok: boolean; sd_organization_id: string; sd_installation_id: string | null }>(
      `/api/v1/pilots/pipeline/${bc_pilot_id}/link`, p).then(r => r.data),
  unlink: (bc_pilot_id: number) =>
    api.delete<UnlinkResult>(`/api/v1/pilots/pipeline/${bc_pilot_id}/link`).then(r => r.data),
};

export const PIPELINE_STATUS_LABEL: Record<PipelineStatus, string> = {
  planned: 'Запланирован',
  active: 'Активен',
  completed: 'Завершён',
  declined: 'Отказ',
  frozen: 'Заморожен',
};

export const PIPELINE_STATUS_COLOR: Record<PipelineStatus, string> = {
  planned: 'bg-slate-800 text-slate-300 border-slate-700',
  active: 'bg-sky-900/40 text-sky-300 border-sky-800',
  completed: 'bg-emerald-900/40 text-emerald-300 border-emerald-800',
  declined: 'bg-rose-900/40 text-rose-300 border-rose-800',
  frozen: 'bg-amber-900/40 text-amber-300 border-amber-800',
};

export const PHASE_LABEL: Record<PilotPhase, string> = {
  poc: 'PoC',
  trial: 'Trial',
  decision: 'Решение',
};

export const PHASE_COLOR: Record<PilotPhase, string> = {
  poc: 'bg-sky-900/40 text-sky-300 border-sky-800',
  trial: 'bg-violet-900/40 text-violet-300 border-violet-800',
  decision: 'bg-amber-900/40 text-amber-300 border-amber-800',
};

export const OUTCOME_LABEL: Record<PilotOutcome, string> = {
  converted: 'Конвертирован',
  cancelled: 'Отменён',
};

export const OUTCOME_COLOR: Record<PilotOutcome, string> = {
  converted: 'bg-emerald-900/40 text-emerald-300 border-emerald-800',
  cancelled: 'bg-rose-900/40 text-rose-300 border-rose-800',
};
